import React from 'react';
import './Skeleton.css';

/**
 * Shimmer placeholder matching layout shapes while content loads.
 *
 * <Skeleton variant="text" width="60%" />
 * <Skeleton variant="card" />
 * <Skeleton variant="line" count={3} />
 *
 * variant: text | line | card | table-row
 * Respects prefers-reduced-motion (shimmer disabled).
 */
const Skeleton = ({ variant = 'text', width, height, count = 1, className = '' }) => {
  const style = {};
  if (width !== undefined) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height !== undefined) style.height = typeof height === 'number' ? `${height}px` : height;

  const item = (key) => (
    <span
      key={key}
      className={`ui-skeleton ui-skeleton--${variant} ${className}`.trim()}
      style={style}
      aria-hidden="true"
    />
  );

  if (count > 1) {
    return (
      <span className="ui-skeleton-group" role="status" aria-busy="true" aria-label="Loading">
        {Array.from({ length: count }, (_, i) => item(i))}
      </span>
    );
  }

  return (
    <span role="status" aria-busy="true" aria-label="Loading">
      {item(0)}
    </span>
  );
};

/**
 * Table loading placeholder — `rows` x `cols` grid of shimmering cells.
 *
 * <TableSkeleton rows={5} cols={4} />
 */
export const TableSkeleton = ({ rows = 5, cols = 4 }) => (
  <div className="ui-table-skeleton" role="status" aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }, (_, r) => (
      <div
        key={r}
        className="ui-table-skeleton__row"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {Array.from({ length: cols }, (_, c) => (
          <span key={c} className="ui-skeleton ui-skeleton--table-row" aria-hidden="true" />
        ))}
      </div>
    ))}
  </div>
);

export default Skeleton;
